import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../../AdminPanel/DataDisplay/useDisplayConfigState';
import {
  getDefaultStandardMetricsConfig,
  StandardMetricsFormValues,
  standardMetricsConfigSchema,
} from './standardMetricsConfig.schema';
import { useStandardMetricsConfigActions } from './useStandardMetricsConfigActions';
import { trackEvent } from '../../../util/datadogTracking';
import { AppName } from '../../../data-models/integrations-app.data-model';

export function useStandardMetricsConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { config, isConnected, isLoading, saveConfig, resetConfig } = useStandardMetricsConfigActions();

  const schema = standardMetricsConfigSchema(isConnected);
  const methods = useForm<StandardMetricsFormValues>({
    resolver: yupResolver(schema as ObjectSchema<StandardMetricsFormValues>),
    defaultValues: getDefaultStandardMetricsConfig(),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    if (config?.config) {
      reset({
        clientId: config.config.clientId || '',
        clientSecret: '',
      });
    }
  }, [config, reset]);

  useEffect(() => {
    if (isDirty && editStatus === 'initial') {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Standard Metrics Config', errors);
      return;
    }
    try {
      const result = await saveConfig(getValues());
      setSuccessMessage(result.message);
      setErrorMessage('');
      setEditStatus('saved');
      reset(getValues());
      trackEvent('integration.connected', { provider: AppName.StandardMetrics, success: true });
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setErrorMessage(
        axiosError?.response?.data?.message || 'Failed to configure Standard Metrics connection'
      );
      setSuccessMessage('');
      trackEvent('integration.connected', { provider: AppName.StandardMetrics, success: false });
    }
  }, [trigger, saveConfig, getValues, errors, reset]);

  const onReset = useCallback(async () => {
    await resetConfig();
    setEditStatus('initial');
    setSuccessMessage('');
    setErrorMessage('');
  }, [resetConfig]);

  return {
    editStatus,
    isConnected,
    isLoading,
    successMessage,
    errorMessage,
    onSave,
    onReset,
    methods,
  };
}
